import React, { useState } from "react";
import { Link, Link as RouterLink } from "react-router-dom";
import {
    IconButton,
    makeStyles,
    Typography
} from "@material-ui/core";
import Table from "@material-ui/core/Table";
import TableContainer
    from "@material-ui/core/TableContainer";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import Paper from "@material-ui/core/Paper";
import FormControl from "@material-ui/core/FormControl";
import InputLabel from "@material-ui/core/InputLabel";
import MenuItem from "@material-ui/core/MenuItem";
import Select from "@material-ui/core/Select";
import Box from "@material-ui/core/Box";
import Grid from "@material-ui/core/Grid";
import TextField from "@material-ui/core/TextField";
import Button from "@material-ui/core/Button";
import { Delete, Edit } from "@material-ui/icons";
import DragHandleIcon from "@material-ui/icons/DragHandle";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import { DatePicker } from "@material-ui/pickers";
import styled from "styled-components";
import {
    useGlobalFilter,
    usePagination,
    useRowSelect,
    useTable,
} from "react-table";
import apiHelper from "../../../util/ApiHelper";
import {
    CustomCheckbox,
    formatDatetime,
    modal
} from "../../../util";
import { useLoading } from "../../../util/LoadingProvider";
import {
    PaginationHandler
} from "../../../util/useManualPagination";
import { toast } from "react-toastify";
import PaginationBarForHandler from "../../common/PaginationBarForHandler";

const useStyles = makeStyles((theme) => ({
    textInput: {
        backgroundColor: "white",
        width: "100%",
        marginBottom: "12px",

        "& input": {
            fontSize: "14px",
        },
    },
    content: {
        "& .ck-editor__editable_inline": {
            minHeight: "200px",
        },
    },
    text: {
        textDecoration: "none",
        color: "#306F9D",
    },
    inactive: {
        color: "#999999",
    },
    buttonGroup: {
        marginTop: "20px",
        textAlign: "right",
        "& button": {
            color: "#306F9D",
            fontSize: "13px",
            textTransform: "inherit",
        },
    },
    box: {
        padding: 0,
        background: "#ffffff",
        paddingTop: "10px",
        paddingBottom: "10px",
        minHeight: "800px",
    },
    innerBox: {
        background: "#ffffff",
        padding: theme.spacing(3),
        marginBottom: theme.spacing(4),
    },
    button: {
        backgroundColor: "#306F9D",
        textTransform: "capitalize",
        color: "white",
        "&:hover": {
            backgroundColor: "#306F9D",
        },
    },
    pagination: {
        "& .MuiTextField-root": {
            width: "35px",
            minWidth: "auto",
        },
        "& .MuiSelect-root": {
            paddingTop: "3px",
            paddingBottom: "3px",
        },
        "& input": {
            padding: "4px 0",
            marginLeft: "10px",
            marginRight: "10px",
        },
        "& button": {
            background: "white",
            color: "#cccccc",
            border: "1px solid #CCCCCC",
            minWidth: "auto",
            width: "24px",
            height: "24px",
            margin: "4px 0",
            "&.left": {
                borderRadius: "4px 0 0 4px",
                marginLeft: "10px",
            },
            "&.right": {
                borderRadius: " 0 4px 4px 0",
            },
        },
    },
}));

const statuses = Object.freeze({
    ACTIVE: "ACTIVE",
    INACTIVE: "INACTIVE",
});

// Context for drag state
const IsDraggingContext = React.createContext(false);

// Reorder function for drag and drop
const reorder = (list, startIndex, endIndex) => {
    const result = Array.from(list);
    const [removed] = result.splice(startIndex, 1);
    result.splice(endIndex, 0, removed);
    return result;
};

// DraggableTableCell component
class DraggableTableCell extends React.Component {
    getSnapshotBeforeUpdate(prevProps) {
        if (!this.ref) return null;

        const isDragStarting =
            this.props.isDragOccurring && !prevProps.isDragOccurring;

        if (!isDragStarting) return null;

        const { width, height } = this.ref.getBoundingClientRect();
        return { width, height };
    }

    componentDidUpdate(prevProps, prevState, snapshot) {
        const ref = this.ref;
        if (!ref) return;

        if (snapshot) {
            if (ref.style.width === snapshot.width) return;
            ref.style.width = `${snapshot.width}px`;
            ref.style.height = `${snapshot.height}px`;
            return;
        }

        if (this.props.isDragOccurring) return;

        if (ref.style.width == null) return;

        ref.style.removeProperty("height");
        ref.style.removeProperty("width");
    }

    setRef = (ref) => {
        this.ref = ref;
    };

    render() {
        return (
            <TableCell
                style={{ paddingTop: 6, paddingBottom: 6, lineHeight: "1.1em", height: "48px" }}
                ref={this.setRef}
                align={this.props.align}
                className={this.props.className}
            >
                {this.props.children}
            </TableCell>
        );
    }
}

// DraggableTableRow component
const DraggableTableRow = ({
    snapshot,
    row,
    provided,
    orderingDisabled,
    isDragging,
}) => {
    return (
        <TableRow
            ref={provided.innerRef}
            {...provided.draggableProps}
            style={{
                ...provided.draggableProps.style,
                background: snapshot.isDragging ? "#f5f5f5" : "white",
            }}
        >
            {row.cells.map((cell, index) => {
                // Skip the dragHandle column (last column)
                return index !== row.cells.length - 1 && (
                    <DraggableTableCell
                        key={index}
                        isDragOccurring={isDragging}
                        className={cell.column.className}
                    >
                        {cell.render("Cell")}
                    </DraggableTableCell>
                );
            })}
            
            {/* Drag handle column */}
            <TableCell
                style={{ paddingBottom: 6, paddingTop: 6, color: "#CCCCCC", cursor: "grab", lineHeight: "1.1em", height: "48px" }}
                {...(!orderingDisabled && { ...provided.dragHandleProps })}
            >
                <DragHandleIcon />
            </TableCell>
        </TableRow>
    );
};

const IndeterminateCheckbox = React.forwardRef(
    ({ indeterminate, ...rest }, ref) => {
        const defaultRef = React.useRef();
        const resolvedRef = ref || defaultRef;

        React.useEffect(() => {
            resolvedRef.current.indeterminate = indeterminate;
        }, [resolvedRef, indeterminate]);

        return <CustomCheckbox type="checkbox"
            ref={resolvedRef} {...rest} />;
    }
);

const StatusFilter = styled.ul`
  list-style: none;
  display: inline-flex;
  padding: 0;

  li {
    button {
      border: 0px;
      background: transparent;
      color: #306f9d;
      font-size: 13px;
      line-height: 15px;
      padding: 0 10px;
      cursor: pointer;

      &.active {
        font-weight: bold;
        color: #333333;
      }
    }

    &:not(:last-of-type) {
      button {
        border-right: 1px solid black;
      }
    }

    &:first-of-type {
      button {
        padding-left: 0;
      }
    }
  }
`;

function FestivalPassListingPage(props) {
    const { match, disabled } = props;
    const classes = useStyles();
    const [status, setStatus] = React.useState(null);
    const [actionType, setActionType] = React.useState("");
    const [keyword, setKeyword] = React.useState("");
    const [data, setData] = React.useState([]);
    const [totalPages, setTotalPages] = React.useState(1);
    const [selectedDate, handleDateChange] = React.useState(null);
    // const [onChange, setOnChange] = React.useState(true);
    const { setLoading } = useLoading();
    // for pagination
    const [paginationHandler] = React.useState(new PaginationHandler());
    const [elementCounts, setElementCounts] = React.useState({
        all: 0,
        active: 0,
        inactive: 0,
    });
    
    // for drag and drop
    const [isDragging, setIsDragging] = useState(false);
    const [orderingDisabled, setOrderingDisabled] = useState(false);

    const CustomLink = React.useMemo(
        () =>
            React.forwardRef((linkProps, ref) => <Link
                ref={ref} {...linkProps} />),
        []
    );

    const handleSelectChange = (e) => {
        setActionType(e.target.value);
    };

    const deleteFestivalPass = async (id) => {
        const decision = await modal.fire({
            title: "Delete Festival Pass",
            text: "Are you sure to delete this Festival Pass?",
            showCancelButton: true,
            confirmButtonText: `Delete`,
        });
        if (decision.isConfirmed) {
            try {
                const result = await apiHelper.hkiff.deleteFestivalPassById(id);
                if (result.data.code === 0) {
                    toast.dark("Festival Pass deleted.");
                    await getFestivalPass();
                }
            } catch (err) {
                console.log(err);
            }
        }
    };

    const columns = React.useMemo(
        () => [
            {
                Header: "Name (English)",
                accessor: "nameEn",
                Cell: ({ row }) => {
                    return (
                        <Link
                            to={`${match.url}/${row.original.id}`}
                            className={classes.text}
                        >
                            {row.original.nameEn}
                        </Link>
                    );
                },
            },
            {
                Header: "Name (Traditional Chinese)",
                accessor: "nameTc",
                Cell: ({ row }) => {
                    return (
                        <Link
                            to={`${match.url}/${row.original.id}`}
                            className={classes.text}
                        >
                            {row.original.nameTc}
                        </Link>
                    );
                },
            },
            {
                Header: "Price",
                accessor: "priceEn",
            },
            {
                Header: "Status",
                accessor: "status",
                disableGlobalFilter: true,
                Cell: ({ row }) => {
                    return row.original.status == "ACTIVE" ? (
                        <span
                            className={classes.text}>Active</span>
                    ) : (
                            <span
                                className={classes.inactive}>Inactive</span>
                        );
                },
            },
            {
                Header: "Last Update",
                accessor: "updateTime",
                disableGlobalFilter: true,
                Cell: ({ row }) => {
                    return formatDatetime(row.original.updateTime);
                },
            },
        ],
        [totalPages]
    );

    const {
        getTableProps,
        headerGroups,
        rows,
        prepareRow,
        setPageSize,
        gotoPage,
        previousPage,
        nextPage,
        canPreviousPage,
        canNextPage,
        selectedFlatRows,
        state: { pageIndex, pageSize },
    } = useTable(
        {
            columns,
            data,
            initialState: { pageIndex: 0, pageSize: 10 },
            manualPagination: true,
            pageCount: totalPages,
        },
        useGlobalFilter,
        usePagination,
        useRowSelect,
        (hooks) => {
            hooks.visibleColumns.push((columns) => [
                // Let's make a column for selection
                {
                    id: "selection",
                    // The header can use the table's getToggleAllRowsSelectedProps method
                    // to render a checkbox
                    Header: ({ getToggleAllRowsSelectedProps }) => (
                        <div>
                            <IndeterminateCheckbox {...getToggleAllRowsSelectedProps()} />
                        </div>
                    ),
                    // The cell can use the individual row's getToggleRowSelectedProps method
                    // to the render a checkbox
                    Cell: ({ row }) => (
                        <div>
                            <IndeterminateCheckbox {...row.getToggleRowSelectedProps()} />
                        </div>
                    ),
                },
                ...columns,
                {
                    id: "action",
                    // The header can use the table's getToggleAllRowsSelectedProps method
                    // to render a checkbox
                    Header: () => <div>Action</div>,
                    // The cell can use the individual row's getToggleRowSelectedProps method
                    // to the render a checkbox
                    Cell: ({ row }) => (
                        <div
                            style={{ display: "inline-flex" }}>
                            <IconButton
                                className={classes.iconBtn}
                                size="small"
                                component={CustomLink}
                                to={`${match.url}/${row.original.id}`}
                            >
                                <Edit fontSize="small" />
                            </IconButton>
                            {!disabled && (
                                <IconButton
                                    className={classes.iconBtn}
                                    size="small"
                                    onClick={() => {
                                        deleteFestivalPass(row.original.id);
                                    }}
                                >
                                    <Delete
                                        fontSize="inherit" />
                                </IconButton>
                            )}
                        </div>
                    ),
                },
                // Drag handle column
                {
                    id: "dragHandle",
                    Header: () => <></>,
                },
            ]);
        }
    );

    const getFestivalPass = React.useCallback(async () => {
        try {
            setLoading(true);
            const result = await apiHelper.hkiff.getFestivalPass(null, 0, 0, 9999999);
            paginationHandler.setPageIndex(0).setRows(result.data.content);
            const content = paginationHandler.filterRowsByAll().getRows();
            const totalPages = paginationHandler.getTotalPages();
            const all = paginationHandler.getStatuslessRows().length;
            const active = paginationHandler
                .getStatuslessRows()
                .filter((x) => x && x.status && x.status=="ACTIVE").length;
            const inactive = all - active;
            setElementCounts({ all, active, inactive });
            setData(content.filter((x) => x));
            setTotalPages(totalPages);
        } catch (err) {
            console.log(err);
        } finally {
            setLoading(false);
        }
    }, [paginationHandler, setLoading]);

    React.useEffect(() => {
        if (paginationHandler) getFestivalPass();
    }, [getFestivalPass]);

    const statusFilterHandler = (targetStatus) => () => {
        setLoading(true);
        setStatus(targetStatus);
        // Disable ordering when filtering
        setOrderingDisabled(targetStatus || keyword || selectedDate ? true : false);
        let target;
        if (targetStatus === statuses.ACTIVE) {
            target = statuses.ACTIVE;
        } else if (targetStatus === statuses.INACTIVE) {
            target = statuses.INACTIVE;
        } else {
            target = null;
        }
        paginationHandler
            .resetRows()
            .setQueryStatus("status", target)
            .filterRowsByAll();
        setData(paginationHandler.getRows());
        gotoPage(0);
        setLoading(false);
    };

    const triggerDateFilter = React.useCallback(() => {
        setLoading(true);
        // Disable ordering when filtering by date
        setOrderingDisabled(selectedDate || keyword || status ? true : false);
        paginationHandler
            .resetRows()
            .setQueryDate("displayDatetime", selectedDate)
            .filterRowsByAll();
        setData(paginationHandler.getRows());
        const all = paginationHandler.getStatuslessRows().length;
        const active = paginationHandler
            .getStatuslessRows()
            .filter((x) => x && x.status).length;
        const inactive = all - active;
        setElementCounts({ all, active, inactive });
        gotoPage(0);
        setLoading(false);
    }, [gotoPage, paginationHandler, selectedDate, setLoading, keyword, status]);

    const triggerSearch = React.useCallback(() => {
        setLoading(true);
        // Disable ordering when searching
        setOrderingDisabled(keyword || status || selectedDate ? true : false);
        paginationHandler
            .resetRows()
            .setQueryString("nameEn", keyword)
            .filterRowsByAll();
        setData(paginationHandler.getRows());
        const all = paginationHandler.getStatuslessRows().length;
        const active = paginationHandler
            .getStatuslessRows()
            .filter((x) => x && x.status).length;
        const inactive = all - active;
        setElementCounts({ all, active, inactive });
        gotoPage(0);
        setLoading(false);
    }, [gotoPage, keyword, paginationHandler, setLoading, status, selectedDate]);

    // Drag and drop callbacks
    const onBeforeDragStart = () => {
        setIsDragging(true);
    };

    const onDragEnd = (result) => {
        setIsDragging(false);

        if (!result.destination) {
            return;
        }

        const items = reorder(data, result.source.index, result.destination.index);
        setData(items);
    };

    // Save ordering function
    const saveOrdering = async () => {
        try {
            const newArray = data.map((o, index) => ({
                id: o.id,
                sequence: index + pageSize * pageIndex,
            }));

            const result = await apiHelper.hkiff.updateFestivalPassSeq(newArray);
            if (result.data.code === 0) {
                toast.dark("Your changes are saved.");
                getFestivalPass();
            }
        } catch (err) {
            console.log(err);
        }
    };

    const handleBulkAction = React.useCallback(async () => {
        switch (actionType) {
            case "DELETE":
                const ids = selectedFlatRows.map((o) => o.original.id);
                if (ids.length > 0) {
                    const decision = await modal.fire({
                        title: "Delete Guest Accreditation",
                        text: `Are you sure to delete ${ids.length} accreditations(s)?`,
                        showCancelButton: true,
                        confirmButtonText: `Delete`,
                    });
                    if (decision.isConfirmed) {
                        try {
                            const promises = ids.map((id) =>
                                apiHelper.hkiff.deleteFestivalPassById(id)
                            );
                            await Promise.all(promises);
                            toast.dark(`${ids.length} programme(s) deleted.`);
                            await getFestivalPass();
                        } catch (err) {
                            console.log(err);
                        }
                    }
                }
                break;
            default:
                break;
        }
    }, [actionType, selectedFlatRows, getFestivalPass]);

    return (
        <div className={classes.content}>
            <Typography variant="h4" gutterBottom>
                Festival Pass
            </Typography>
            <form>
                <Grid
                    container
                    justify="space-between"
                    style={{ paddingBottom: "10px" }}
                >
                    <Grid container item xs={6}>
                        <Box width="100%" component="span" display="flex">
                            <Grid container item xs={4}>
                                <Button
                                    variant="contained"
                                    color="primary"
                                    onClick={saveOrdering}
                                    disabled={orderingDisabled || disabled}
                                >
                                    Save Changes
                                </Button>
                            </Grid>
                        </Box>
                    </Grid>
                </Grid>

                <Grid
                    container
                    justify="space-between"
                    style={{ paddingBottom: "10px" }}
                >
                    <Grid container item xs={6} md={6}>
                        <Box
                            width="100%"
                            component="span"
                            display="flex"
                            alignItems="center"
                        >
                            <StatusFilter>
                                <li>
                                    <button
                                        className={status === null ? "active" : ""}
                                        type="button"
                                        onClick={statusFilterHandler(null)}
                                    >
                                        <Typography variant="p">All
                                ({elementCounts.all})</Typography>
                                    </button>
                                </li>
                                <li>
                                    <button
                                        className={status === statuses.ACTIVE ? "active" : ""}
                                        type="button"
                                        onClick={statusFilterHandler(statuses.ACTIVE)}
                                    >
                                        <Typography variant="p">
                                            Active
                                ({elementCounts.active})
                            </Typography>
                                    </button>
                                </li>
                                <li>
                                    <button
                                        className={status === statuses.INACTIVE ? "active" : ""}
                                        type="button"
                                        onClick={statusFilterHandler(statuses.INACTIVE)}
                                    >
                                        <Typography variant="p">
                                            Inactive
                                ({elementCounts.inactive})
                            </Typography>
                                    </button>
                                </li>
                            </StatusFilter>
                        </Box>
                    </Grid>
                    <Grid container item xs={6} md={6}>
                        <Box
                            width="100%"
                            component="span"
                            display="flex"
                            justifyContent="flex-end"
                            alignItems="center"
                        >
                            <Box display="flex">
                                <Button
                                    variant="contained"
                                    color="primary"
                                    type="button"
                                    component={RouterLink}
                                    disabled={disabled}
                                    to={`${match.url}/new`}
                                >
                                    Add New Festival Pass
                                </Button>
                            </Box>
                            <Box display="flex">
                                <TextField
                                    id="outlined-error"
                                    label="Search"
                                    variant="outlined"
                                    margin="dense"
                                    value={keyword}
                                    onChange={(e) => setKeyword(e.target.value)}
                                />
                            </Box>
                            <Box display="flex">
                                <Button
                                    variant="outlined"
                                    color="primary"
                                    type="button"
                                    margin="dense"
                                    onClick={triggerSearch}
                                >
                                    Search
                                </Button>
                            </Box>
                        </Box>
                    </Grid>
                </Grid>
                <Grid container xs={12}>
                    <TableContainer
                        component={Paper}
                        style={{
                            boxShadow: "none",
                            border: "1px solid #cccccc",
                        }}
                    >
                        <fieldset disabled={disabled}
                            className="fieldset">
                            <Table
                                aria-label="simple table"
                                component="ul"
                                style={{ margin: "0" }}
                                {...getTableProps()}
                            >
                                <colgroup>
                                    <col
                                        style={{ width: "5%" }} />
                                    <col
                                        style={{ width: "22%" }} />
                                    <col
                                        style={{ width: "22%" }} />
                                    <col
                                        style={{ width: "10%" }} />
                                    <col
                                        style={{ width: "10%" }} />
                                    <col
                                        style={{ width: "10%" }} />
                                    <col
                                        style={{ width: "5%" }} />
                                    <col
                                        style={{ width: "5%" }} />
                                </colgroup>
                                <TableHead component="div">
                                    {headerGroups.map((headerGroup) => (
                                        <TableRow {...headerGroup.getHeaderGroupProps()}>
                                            {headerGroup.headers.map((column) => (
                                                <TableCell
                                                    {...column.getHeaderProps()}
                                                    className={`${column.className ?? ""}`}
                                                    style={{
                                                        paddingTop: 6,
                                                        paddingBottom: 6,
                                                        lineHeight: "1.1em",
                                                        height: "48px",
                                                    }}
                                                >
                                                    {column.render("Header")}
                                                </TableCell>
                                            ))}
                                        </TableRow>
                                    ))}
                                </TableHead>
                                <DragDropContext
                                    onBeforeDragStart={onBeforeDragStart}
                                    onDragEnd={onDragEnd}
                                >
                                    <Droppable droppableId="festival-pass-table-droppable">
                                        {(provided, snapshot) => (
                                            <TableBody
                                                component="div"
                                                ref={provided.innerRef}
                                                {...provided.droppableProps}
                                            >
                                                <IsDraggingContext.Provider value={isDragging}>
                                                    {rows.map((row, index) => {
                                                        prepareRow(row);
                                                        return (
                                                            <Draggable
                                                                key={row.original.id}
                                                                draggableId={String(row.original.id)}
                                                                index={index}
                                                                isDragDisabled={disabled || orderingDisabled}
                                                            >
                                                                {(provided, snapshot) => (
                                                                    <DraggableTableRow
                                                                        key={row.original.id}
                                                                        row={row}
                                                                        provided={provided}
                                                                        snapshot={snapshot}
                                                                        orderingDisabled={orderingDisabled}
                                                                        isDragging={isDragging}
                                                                    />
                                                                )}
                                                            </Draggable>
                                                        );
                                                    })}
                                                </IsDraggingContext.Provider>
                                                {provided.placeholder}
                                            </TableBody>
                                        )}
                                    </Droppable>
                                </DragDropContext>
                            </Table>
                        </fieldset>
                    </TableContainer>
                </Grid>
                <Grid
                    container
                    justify="flex-end"
                    style={{
                        paddingRight: "25px",
                        marginTop: "15px"
                    }}
                >
                    <div className={classes.flexRow}>
                        <div></div>
                        <PaginationBarForHandler
                            pageSize={pageSize}
                            setPageSize={setPageSize}
                            setData={setData}
                            setTotalPages={setTotalPages}
                            paginationHandler={paginationHandler}
                            gotoPage={gotoPage}
                            pageIndex={pageIndex}
                            canPreviousPage={canPreviousPage}
                            previousPage={previousPage}
                            nextPage={nextPage}
                            canNextPage={canNextPage}
                            elementCounts={elementCounts}
                            status={status}
                        />
                    </div>
                </Grid>
            </form>
        </div>
    );
}

export default FestivalPassListingPage;
